/**
 * Speaker bubble — left/right alignment + color differentiation for two-
 * speaker dialogues. Matches the Duolingo Stories convention.
 *
 * No avatars; the visual rhythm comes from alignment + Tailwind palette.
 */

interface Props {
  /** "you" = the learner's line. "them" = the partner / interlocutor. */
  side: 'you' | 'them';
  /** Primary text (English). */
  primary: string;
  /** Secondary text (Japanese kana). Optional — front face shows EN only. */
  secondary?: string;
}

export function SpeakerBubble({ side, primary, secondary }: Props) {
  const isYou = side === 'you';
  const align = isYou ? 'items-start' : 'items-end';
  const bubbleAlign = isYou ? 'self-start' : 'self-end';
  const bubbleColor = isYou
    ? 'bg-slate-100 text-slate-800 border-slate-200'
    : 'bg-amber-50 text-amber-900 border-amber-200';
  const labelColor = isYou ? 'text-slate-500' : 'text-amber-700';
  const label = isYou ? 'You' : 'Them';

  return (
    <div className={`flex flex-col ${align} gap-1 max-w-[85%] ${bubbleAlign}`}>
      <span className={`text-[10px] font-semibold uppercase tracking-wider ${labelColor}`}>
        {label}
      </span>
      <div className={`px-3 py-2 rounded-2xl border ${bubbleColor}`}>
        <p className="text-sm font-medium leading-snug">{primary}</p>
        {secondary && (
          <p className="text-sm font-japanese tracking-japanese mt-1 opacity-80">
            {secondary}
          </p>
        )}
      </div>
    </div>
  );
}
